import { useState, useEffect, useCallback } from "react";

export interface ToastState {
  message: string;
  type: "info" | "success" | "warning" | "error";
  visible: boolean;
}

interface ToastProps {
  toast: ToastState;
  onDismiss: () => void;
  autoDismissMs?: number;
}

export function Toast({ toast, onDismiss, autoDismissMs = 5000 }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  const dismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(onDismiss, 300);
  }, [onDismiss]);

  useEffect(() => {
    if (!toast.visible) return;
    setIsExiting(false);
    const timer = setTimeout(dismiss, autoDismissMs);
    return () => clearTimeout(timer);
  }, [toast.visible, toast.message, autoDismissMs, dismiss]);

  if (!toast.visible) return null;

  const iconMap: Record<ToastState["type"], string> = {
    info: "ri-information-line",
    success: "ri-checkbox-circle-line",
    warning: "ri-error-warning-line",
    error: "ri-close-circle-line",
  };

  const bgMap: Record<ToastState["type"], string> = {
    info: "bg-background-50 border-background-200/70",
    success: "bg-background-50 border-green-200",
    warning: "bg-background-50 border-amber-200",
    error: "bg-background-50 border-red-200",
  };

  const iconColorMap: Record<ToastState["type"], string> = {
    info: "text-primary-500",
    success: "text-green-600",
    warning: "text-amber-600",
    error: "text-red-600",
  };

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
        isExiting ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
      }`}
    >
      <div
        className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl border shadow-lg backdrop-blur-md ${bgMap[toast.type]} max-w-md`}
      >
        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0`}>
          <i className={`${iconMap[toast.type]} ${iconColorMap[toast.type]} text-xl`}></i>
        </div>
        <p className="text-sm text-foreground-900 font-medium flex-1 whitespace-nowrap">{toast.message}</p>
        <button
          onClick={dismiss}
          className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex-shrink-0"
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

export function useToast() {
  const [toast, setToast] = useState<ToastState>({
    message: "",
    type: "info",
    visible: false,
  });

  const showToast = useCallback((message: string, type: ToastState["type"] = "info") => {
    setToast({ message, type, visible: true });
  }, []);

  const dismissToast = useCallback(() => {
    setToast((prev) => ({ ...prev, visible: false }));
  }, []);

  return { toast, showToast, dismissToast };
}